import { useStyleConfigV2 } from '@tectonic/elemason-components';
import { ElemasonWidgetActionType } from '@tectonic/types';
import { Clickable } from '@tectonic/uikit';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useEffect, type FC, type MouseEvent } from 'react';
import {
  useElemasonAnalyticsContext,
  useElemasonPageContext,
} from '../../../contexts';
import { ElemasonFragmentProvider } from '../../../contexts/ElemasonFragmentContext';
import { Cell } from '../../../core/Cell';
import { useProfileInfo } from './useProfileInfo';

import type {
  ElemasonProfileInfoWidget,
  ElemasonWidgetAction,
} from '@tectonic/types';

interface ProfileInfoWidgetProps {
  widget: ElemasonProfileInfoWidget;
}

const ProfileInfoWidget: FC<ProfileInfoWidgetProps> = ({ widget }) => {
  const {
    fragment,
    isLoading,
    profileData,
    fragmentValue,
    productProfileSchema,
  } = useProfileInfo(widget);

  const { dispatch } = useElemasonPageContext();
  const analyticsContext = useElemasonAnalyticsContext();
  const [style, className] = useStyleConfigV2(widget.config?.container ?? {});

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();

    dispatch({
      type: ElemasonWidgetActionType.GLOBAL_DRAWER_OPEN,
      payload: {
        slug: 'profile-data-edit',
        data: {
          profileData,
          productProfileSchema,
        },
        exit: 'bottom',
        entry: 'bottom',
      },
    });
  };

  useEffect(() => {
    if (
      !isLoading &&
      isEmpty(profileData) &&
      !isEmpty(productProfileSchema) &&
      widget.config?.autoOpenDrawer
    ) {
      dispatch(
        {
          type: ElemasonWidgetActionType.GLOBAL_DRAWER_OPEN,
          payload: {
            slug: 'profile-data-edit',
            data: {
              profileData,
              productProfileSchema,
            },
            exit: 'bottom',
            entry: 'bottom',
          },
        } satisfies ElemasonWidgetAction,
        analyticsContext
      );
    }
  }, [
    profileData,
    productProfileSchema,
    widget.config?.autoOpenDrawer,
    isLoading,
  ]);

  return (
    <Clickable
      style={style}
      onClick={onClick}
      className={clsx('flex', className)}
    >
      <ElemasonFragmentProvider
        value={fragmentValue({
          profileData: isEmpty(profileData) ? undefined : profileData,
          productProfileSchema,
          readableProfileData: profileData
            ? Object.entries(profileData)
                .map(
                  ([k, v]) =>
                    `${productProfileSchema?.schema?.properties?.[k]?.title}: ${v}`
                )
                .join('; ')
            : '',
        })}
      >
        {fragment?.cells.map((cell) => <Cell key={cell.id} cell={cell} />)}
      </ElemasonFragmentProvider>
    </Clickable>
  );
};

export { ProfileInfoWidget };
